import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Modal } from '../../../@domain/modal/modal.enum';
import { MetaService } from '../../../@domain/seo/meta.service';
import { UserService } from '../../../@domain/user/application/user.service';
import { VideoService } from '../../../@domain/video/application/video.service';
import { VideoInfoSort } from '../../../@generated';
import { CategoriesComponent } from '../../../@ui/app/content/sections/landing-page/categories/categories.component';
import { EventsSectionComponent } from '../../../@ui/app/content/sections/landing-page/events-section/events-section.component';
import { HistoryComponent } from '../../../@ui/app/content/sections/landing-page/history/history.component';
import { LatestVideosComponent } from '../../../@ui/app/content/sections/landing-page/latest-videos/latest-videos.component';
import { NewsComponent } from '../../../@ui/app/content/sections/landing-page/news/news.component';
import { OrganizationsSectionComponent } from '../../../@ui/app/content/sections/landing-page/organizations-section/organizations-section.component';
import { ProjectsSectionComponent } from '../../../@ui/app/content/sections/landing-page/projects-section/projects-section.component';
import { SectionHeaderComponent } from '../../../@ui/app/global/section-header/section-header.component';
import { WelcomeModalComponent } from '../../../@ui/app/modals/welcome-modal/welcome-modal.component';
import { AppRoute } from '../../../routing/types/app-route';

@Component({
    selector: 'app-landing',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CategoriesComponent, HistoryComponent, LatestVideosComponent, EventsSectionComponent, SectionHeaderComponent, NewsComponent, OrganizationsSectionComponent, ProjectsSectionComponent, AsyncPipe, WelcomeModalComponent],
    styles: `
        @use "functions" as *;
        @use "variables" as *;

        :host {
            width: 100%;
        }

            .sections-container {
            padding-top: 45px;
            @media screen and (max-width: 850px) {
            padding-top: 45px;
            }
        }


        .last-section {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 3rem;

            @include break(996px) {
                grid-template-columns: repeat(1, 1fr);
                grid-row-gap: 20px;
                gap: 0;
            }
        }

        .section-placeholder {
            min-height: 540px;
            width: 100%;
        }

        section {
            width: 100%;
            margin-bottom: 45px;

            &.bg-light {
                padding: 45px 0;
            }

            @include break(996px) {
                padding: 0 1rem;

                &.bg-light {
                    padding: 45px 1rem;
                }
            }

            @include break(670px) {
                padding: 0;

                &.bg-light {
                    padding: 45px 0;
                }
            }
        }
    `,
    template: `


        <div class="sections-container">
            <section>
                <div class="container">
                    <app-section-header class="categories-header first-header" headline="BROWSE CATEGORIES" />
                    <app-categories ngSkipHydration/>
                </div>
            </section>

            @if (isAuthenticated() && (usersHistory$ | async); as usersHistory) {
                @if (usersHistory.results.length) {
                    <section class="bg-light">
                        <div class="container">
                            <app-section-header headline="Continue watching" [link]="AppRoutes.History"
                                                buttonText="VIEW ALL"/>
                            <app-history [history]="usersHistory.results"/>
                        </div>
                    </section>
                }
            }
            <section>
                <div class="container" >
                    <app-section-header headline="RECENTLY ADDED VIDEOS" [link]="AppRoutes.Videos" buttonText="VIEW ALL"/>
                    <app-latest-videos/>
                </div>
            </section>

            <!-- <section>
                <div class="container mb-2">
                    <app-section-header headline="Trending" [link]="AppRoutes.Videos" buttonText="VIEW ALL"
                                        [linkParams]="{ sort : VideoInfoSort.TotalViews}"/>
                    <app-trending-videos/>
                </div>
            </section> -->

            <section >
                <div class="container">
                    <app-section-header headline="RECENTLY ADDED EVENTS" [link]="AppRoutes.Events" buttonText="VIEW ALL"/>
                    <app-events-section/>
                </div>
            </section>

            <section>
                <div class="container">
                    <app-section-header headline="BROWSE PROJECTS" buttonText="VIEW ALL" [link]="AppRoutes.Events" />
                    <app-projects-section/>
                </div>
            </section>

            <section>
                <div class="container">
                    <app-section-header headline="BROWSE ORGANIZATIONS" buttonText="VIEW ALL" [link]="AppRoutes.Events"/>
                    <app-organizations-section/>
                </div>
            </section>

            <section>
                <div class="container">
                    <app-section-header headline="LATEST NEWS" />
                    <app-news/>
                </div>
            </section>

        </div>


        <app-welcome-modal/>
    `
})
export class LandingPage {
    private userService = inject(UserService);
    private metaService = inject(MetaService);
    private videoService = inject(VideoService);

    public user = this.userService.user;
    public isAuthenticated = this.userService.isAuthenticated;
    public usersHistory$ = this.videoService.getWatchHistory(1, 4);
    public AppRoutes = AppRoute;
    public Modal = Modal;

    constructor() {
        // Commented because it was lowering LCP metric, and was showing for 1 year
        // afterNextRender(() => {
        //     if (localStorage.getItem('seenModal') !== 'true') {
        //         this.modalService.open(Modal.WelcomeNotice);
        //     }
        // })

        this.metaService.set({
            title: 'Videolectures - Online Learning Platform',
            description: 'Videolectures is an online learning platform with thousands of free video lectures from the world\'s top lecturers. Join us and start learning today!',
            image: undefined,
            index: true
        })
    }

    protected readonly VideoInfoSort = VideoInfoSort;
}
